import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { Face } from '../components/primitives'
import { radius } from '../components/primitives/shape'
import type { Agent, AgentPlan, Channel, OrganisationAgent, PlanItem } from '../api/types'
import { useCanHover } from '../app/useViewport'
import { activityLabel } from './activity'
import { displayName, roomName } from './format'
import { newTabFor, openLink } from './openLink'

/** How far a sheet is pulled down before letting go of it closes it. */
const SWIPE_TO_CLOSE = 80

/** How many held files About lists before "Show more". */
const CLAIMS_SHOWN = 3

/** A plan step's mark, in mono beside it, as the sidebar shows progress. */
const PLAN_MARK: Record<PlanItem['state'], { word: string; color: string }> = {
  done: { word: 'done', color: 'var(--online)' },
  doing: { word: 'now', color: 'var(--accent)' },
  pending: { word: '', color: 'var(--text-faint)' },
}

const BUTTON: CSSProperties = {
  display: 'flex', justifyContent: 'center', alignItems: 'center', flex: '1 1 auto', padding: '10px 16px',
  borderRadius: radius.action, border: '1px solid var(--border-strong)', fontSize: 14, fontWeight: 700,
  color: 'var(--text)', cursor: 'pointer', textDecoration: 'none',
}

const SECTION: CSSProperties = { marginTop: 24, paddingTop: 14, borderTop: '1px solid var(--border)' }
const SECTION_HEADING: CSSProperties = { margin: '0 0 12px', fontSize: 12.5, fontWeight: 500, color: 'var(--text-meta)' }
const TERM: CSSProperties = { color: 'var(--text-meta)' }
const DEFINITION: CSSProperties = { margin: 0, minWidth: 0, overflowWrap: 'anywhere' }

/**
 * One agent or person. One's own agent shows what the room and the
 * organisation stream know about it and offers its direct message; anyone else
 * shows who they are and a mention, because a direct message reaches only
 * one's own agents.
 */
export function Profile({ name, agent, yours, plan, channels, compact, onDirectMessage, onMention }: {
  name: string
  /** Its line in this room's roster; none for a person. */
  agent: Agent | undefined
  /** Set when it is one of the person's own agents, from the organisation stream. */
  yours: OrganisationAgent | undefined
  plan: AgentPlan | undefined
  /** To name the room a run started it from. */
  channels: Channel[]
  compact: boolean
  onDirectMessage: (name: string) => void
  onMention: (name: string) => void
}) {
  const canHover = useCanHover()
  const summary = agent?.summary ?? yours?.summary
  const surface = agent?.surface ?? yours?.surface
  const link = openLink({
    open_url: agent?.open_url ?? yours?.open_url,
    remote_url: agent?.remote_url ?? yours?.remote_url,
  }, canHover)
  const startedIn = channels.find((channel) => channel.id === agent?.started_by_run?.channel_id)
  const steps = yours && plan ? plan.items : []
  const done = steps.filter((step) => step.state === 'done').length
  const state = !yours ? null
    : yours.waiting > 0 ? 'waiting on you'
    : activityLabel(agent?.online ?? yours.online, agent?.activity ?? yours.activity)
  const nickname = agent?.nickname ?? yours?.nickname
  const title = agent?.title ?? yours?.title
  // A worktree is often named after its branch, and then once is enough.
  const worktreeAndBranch = [...new Set([agent?.worktree, agent?.branch].filter(Boolean))].join(' · ')

  return (
    <div style={{ padding: compact ? '4px 20px 22px' : '22px 22px 26px' }}>
      <Face name={name} size={compact ? 72 : 120} avatarKey={agent?.avatar_key ?? yours?.avatar_key} />
      <h3 style={{ margin: '14px 0 3px', font: `700 19px var(${nickname ? '--font-interface' : '--font-machine'})`, overflowWrap: 'anywhere' }}>
        {displayName(name, nickname)}
      </h3>
      {/* The handle is what a tag needs, so it stays in sight under a nickname. */}
      {nickname && (
        <p style={{ margin: 0, fontFamily: 'var(--font-machine)', fontSize: 12, color: 'var(--text-faint)', overflowWrap: 'anywhere' }}>{name}</p>
      )}
      {title && (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-soft)', overflowWrap: 'anywhere' }}>{title}</p>
      )}
      {yours && worktreeAndBranch && (
        <p style={{ margin: 0, font: '400 12px var(--font-machine)', color: 'var(--text-meta)' }}>{worktreeAndBranch}</p>
      )}
      {(state || agent?.role) && (
        <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-meta)' }}>
          {state ? (
            <>
              <span style={{ color: state === 'working' ? 'var(--online)' : 'var(--text-soft)' }}>{state}</span>
              {surface && ` · ${surface}`}
            </>
          ) : agent?.role}
        </p>
      )}
      {summary && <p style={{ margin: '12px 0 0', fontSize: 14, lineHeight: 1.5, color: 'var(--text-soft)' }}>{summary}</p>}

      <div style={{ display: 'flex', flexDirection: compact ? 'column' : 'row', flexWrap: 'wrap', gap: 10, marginTop: 18 }}>
        {yours && (
          <button
            type="button"
            onClick={() => onDirectMessage(name)}
            style={{ ...BUTTON, background: 'var(--accent)', borderColor: 'var(--accent)', color: 'var(--ground)' }}
          >
            Send a direct message
          </button>
        )}
        <button type="button" onClick={() => onMention(name)} style={BUTTON}>Mention in the room</button>
        {yours && link && <a href={link} {...newTabFor(link)} style={BUTTON}>Open in Claude app</a>}
      </div>

      {yours && (
        <section aria-label="About" style={SECTION}>
          <h4 style={SECTION_HEADING}>About</h4>
          <dl style={{ display: 'grid', gridTemplateColumns: '96px minmax(0, 1fr)', gap: '10px 12px', margin: 0, fontSize: 13.5 }}>
            <dt style={TERM}>Owner</dt>
            <dd style={DEFINITION}>You</dd>
            {startedIn && (
              <>
                <dt style={TERM}>Started</dt>
                <dd style={DEFINITION}>From #{roomName(startedIn)}</dd>
              </>
            )}
            {surface && (
              <>
                <dt style={TERM}>Runs in</dt>
                <dd style={DEFINITION}>{surface}</dd>
              </>
            )}
            {agent && agent.claims.length > 0 && (
              <>
                <dt style={TERM}>Holds</dt>
                <dd style={{ ...DEFINITION, font: '400 12.5px/1.7 var(--font-machine)' }}>
                  <HeldFiles paths={agent.claims} />
                </dd>
              </>
            )}
          </dl>
        </section>
      )}

      {steps.length > 0 && (
        <section aria-label="Plan" style={SECTION}>
          <h4 style={SECTION_HEADING}>Plan · {done} of {steps.length}</h4>
          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 9 }}>
            {steps.map((step, index) => (
              <li key={index} style={{ display: 'flex', gap: 12, fontSize: 13.5, lineHeight: 1.45 }}>
                <span style={{ width: 40, flex: 'none', font: '600 11px/1.75 var(--font-machine)', color: PLAN_MARK[step.state].color }}>
                  {PLAN_MARK[step.state].word}
                </span>
                <span style={{ color: step.state === 'done' ? 'var(--text-dim)' : 'var(--text-soft)' }}>{step.text}</span>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  )
}

/**
 * The profile on a phone: a sheet over the dimmed room. A tap on the dim, a
 * swipe down or Escape (the workspace's) closes it. Focus goes to its first
 * control while it is open and back to whatever opened it when it goes.
 */
export function ProfileSheet({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const dim = useRef<HTMLDivElement>(null)
  const sheet = useRef<HTMLDivElement>(null)
  const [swipe, setSwipe] = useState<{ from: number; pulled: number } | null>(null)

  useEffect(() => {
    const opener = document.activeElement
    sheet.current?.querySelector<HTMLElement>('button, a[href]')?.focus()
    return () => {
      if (opener instanceof HTMLElement) opener.focus()
    }
  }, [])

  return (
    <div
      ref={dim}
      role="dialog"
      aria-modal="true"
      aria-label="Profile"
      onMouseDown={(event) => {
        if (event.target === dim.current) onClose()
      }}
      style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,.7)' }}
    >
      <div
        ref={sheet}
        onTouchStart={(event) => {
          // Only from the top: further down the sheet, a drag is a scroll.
          if (event.currentTarget.scrollTop === 0) setSwipe({ from: event.touches[0].clientY, pulled: 0 })
        }}
        onTouchMove={(event) => {
          const at = event.touches[0].clientY
          setSwipe((current) => current && { ...current, pulled: Math.max(0, at - current.from) })
        }}
        onTouchEnd={() => {
          if (swipe && swipe.pulled > SWIPE_TO_CLOSE) onClose()
          setSwipe(null)
        }}
        onTouchCancel={() => setSwipe(null)}
        style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: '88%', overflowY: 'auto',
          overscrollBehaviorY: 'contain', paddingBottom: 'var(--safe-bottom)',
          background: 'var(--ground-raised)', borderTop: '1px solid var(--border-strong)',
          borderRadius: `${radius.xl} ${radius.xl} 0 0`, boxShadow: 'var(--shadow-overlay)',
          transform: `translateY(${swipe?.pulled ?? 0}px)`, transition: swipe ? 'none' : 'transform .18s ease',
        }}
      >
        <div aria-hidden="true" style={{ width: 36, height: 4, margin: '8px auto 10px', borderRadius: radius.xs, background: 'var(--border-strong)' }} />
        {children}
      </div>
    </div>
  )
}

/**
 * The files an agent holds, three until asked for the rest. The file's own name
 * always shows whole; its folder gives way first, cut from its start, so the
 * folder nearest the file is the part that stays.
 */
function HeldFiles({ paths }: { paths: string[] }) {
  const [expanded, setExpanded] = useState(false)
  const shown = expanded ? paths : paths.slice(0, CLAIMS_SHOWN)
  const hidden = paths.length - CLAIMS_SHOWN
  return (
    <>
      {shown.map((path) => {
        const cut = path.lastIndexOf('/', path.length - 2)
        const folder = cut < 0 ? '' : path.slice(0, cut + 1)
        const file = path.slice(cut + 1)
        return (
          <div key={path} title={path} style={{ display: 'flex', minWidth: 0, whiteSpace: 'nowrap' }}>
            {folder && (
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', direction: 'rtl', color: 'var(--text-faint)' }}>
                <bdi dir="ltr">{folder}</bdi>
              </span>
            )}
            <span style={{ flex: 'none', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file}</span>
          </div>
        )
      })}
      {hidden > 0 && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
          style={{ marginTop: 2, padding: '2px 0', font: '600 12.5px var(--font-interface)', color: 'var(--accent)', cursor: 'pointer' }}
        >
          {expanded ? 'Show less' : `Show ${hidden} more`}
        </button>
      )}
    </>
  )
}
